/**
 * IMPORTS
 */
import { useCallback, useState } from "react";

import { FileX2 } from "lucide-react";

import Avatar from "@components/avatar/avatar";
import { CardInfo } from "@components/card-info";
import { NotificacoesSino } from "@components/notificacoes/notificacoes-sino";
import { PaginationControls } from "@components/pagination-controls/pagination-controls";
import { Spinner } from "@components/spinner/spinner";
import { Tabs } from "@components/tabs";
import { TutorialVideoModal } from "@components/tutorial-video-modal";

import { SearchInputDashboard } from "@pages/client/dashboard/components/inpunt-search-dashboard/inpunt-search-dashboard";
import { ModeloCard } from "@pages/client/dashboard/components/modelo-card";
import { ModeloCardSkeleton } from "@pages/client/dashboard/components/modelo-card-skeleton";

import { useModelosPorCliente } from "@/hooks/modelos/buscar-modelos-por-clientes";
import { useGetTotalCreditosPorCliente } from "@/hooks/vendas-creditos/listar-total-creditos-por-cliente";
import { useBuscarVideoTutorial } from "@/hooks/videos-tutorias/buscar-video-tutorial";

import { useStoreZustandModelos } from "@/store-zustand/modelos/modelos";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import type { IModeloUnicoProps } from "@/store-zustand/cliente/details-modelo/index.d";

import ImageAvatar from "@/common/assets/png/perfil-default-letscom.png";
import { FEATURES, useFeature } from "@/utils/auth/features";
import { handleLimitTextdisplayByAmount } from "@/utils/text-limit";

const SKELETON_COUNT = 8;

const ModelPage = () => {
  const { user } = useStoreZustandUserAuth();
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const {
    currentPage,
    setCurrentPage,
    paginations,
    lastPage,
    isLoading,
    setSearchItem,
    itemsPerPage,
    totalItemsPage,
  } = useStoreZustandModelos();

  const idParams = user?.tipo_login === "subordinado" ? user?.cliente_principal?.id : user?.id;

  const { modelosPorClientes } = useModelosPorCliente(Number(idParams));

  const podeVerSaldoCreditos = useFeature(FEATURES.VISUALIZAR_SALDO_CREDITOS);

  const { vendasCreditos, isLoading: isLoadingVendas } = useGetTotalCreditosPorCliente(
    Number(idParams),
    podeVerSaldoCreditos
  );

  const {
    videoUrl,
    videoNome,
    isLoading: isLoadingVideoTutorial,
    isError: isErrorVideoTutorial,
  } = useBuscarVideoTutorial(isTutorialModalOpen);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchItem(value);
    },
    [setSearchItem]
  );

  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const effectiveLastPage =
        lastPage > 1 ? lastPage : Math.max(1, Math.ceil(totalItemsPage / itemsPerPage));

      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? Math.min(effectiveLastPage, currentPage + 1)
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, lastPage, totalItemsPage, itemsPerPage, setCurrentPage]
  );

  const showPaginationFooter = totalItemsPage > 0 && !isLoading;

  return (
    <>
      {isLoadingVendas ? (
        <Spinner />
      ) : (
        <>
          <Tabs defaultValue="all">
            <div className="flex items-center">
              <h2 className="p-4 text-2xl font-medium text-black">Dashboard</h2>
              <div className="ml-auto mr-4 flex items-center gap-2">
                <p className="ml-1 mt-1 text-2xl font-medium text-black">
                  Olá,{" "}
                  {handleLimitTextdisplayByAmount({
                    limit: 20,
                    text: user?.nome!,
                  })}
                </p>
              </div>
              <NotificacoesSino />
              <div className="flex justify-end p-4">
                <Avatar alt={user?.nome} size="small" name={user?.nome} src={ImageAvatar} />
              </div>
            </div>

            <div className="mb-4 flex flex-wrap p-4">
              {vendasCreditos.length === 0
                ? null
                : vendasCreditos.map((item, index) => (
                    <div key={index} className={index > 0 ? "ml-4" : ""}>
                      <CardInfo item={item} />
                    </div>
                  ))}
            </div>

            <div className="flex w-full flex-row flex-wrap items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <SearchInputDashboard
                  onSearch={handleSearch}
                  loading={isLoading}
                  placeholder="Pesquisar por nome de modelo…"
                />
              </div>

              <button
                type="button"
                className="shrink-0 whitespace-nowrap rounded bg-amber-600 px-4 py-2 text-white transition-colors hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
                onClick={() => setIsTutorialModalOpen(true)}
              >
                Tutorial de Solicitação
              </button>
            </div>

            <div className="flex items-center gap-2 px-4 pb-2">
              <h3 className="text-lg font-semibold text-gray-900">Seus Modelos</h3>
              {!isLoading && totalItemsPage > 0 && (
                <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-700">
                  {totalItemsPage}
                </span>
              )}
            </div>

            {isLoading ? (
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <ModeloCardSkeleton key={index} />
                ))}
              </div>
            ) : modelosPorClientes.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center px-4 py-16 text-gray-500">
                <FileX2 className="mb-4 h-10 w-10 opacity-60" aria-hidden="true" />
                <p className="text-center text-base">Nenhum modelo encontrado para este cliente.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {modelosPorClientes.map((item) => (
                  <ModeloCard key={item.id} item={item as IModeloUnicoProps} />
                ))}
              </div>
            )}
          </Tabs>

          {showPaginationFooter && (
            <div className="px-4 pb-8">
              <PaginationControls
                currentPage={currentPage}
                lastPage={lastPage}
                paginations={paginations}
                totalItems={totalItemsPage}
                itemsPerPage={itemsPerPage}
                onGoToPage={onGoToPage}
              />
            </div>
          )}
        </>
      )}

      <TutorialVideoModal
        open={isTutorialModalOpen}
        onClose={() => setIsTutorialModalOpen(false)}
        videoUrl={videoUrl}
        videoNome={videoNome}
        isLoading={isLoadingVideoTutorial}
        isError={isErrorVideoTutorial}
      />
    </>
  );
};

/**
 * EXPORTS
 */
export { ModelPage };
